import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Button } from '../../components/ui/Button'
import { Card, CardHeader } from '../../components/ui/Card'
import { SkeletonTable } from '../../components/ui/Skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/Table'
import * as audit from '../../services/storeAuditService'
import {
  TX_CHANNELS,
  buildAuditListSearch,
  parseAuditListSearch,
  storeAuditTransactionPath,
  transactionDetailRef,
  txKindLabel,
  type AuditNavState,
  type AuditTab,
  type TxChannel,
} from './storeAuditRoutes'

const TX_LIMIT = 20
const ORD_LIMIT = 20

export default function StoreAuditPage() {
  const { storeId: storeIdParam } = useParams()
  const storeId = storeIdParam != null ? Number(storeIdParam) : NaN
  const invalidStore = !Number.isFinite(storeId) || storeId < 1

  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()
  const navState = (location.state as AuditNavState | null) ?? {}

  const listSearch = useMemo(() => parseAuditListSearch(searchParams), [searchParams])
  const { tab, channel, txPage, ordPage, ordStatus, ordPayment } = listSearch

  const storeName = navState.storeName ?? `Store #${storeId}`
  const organisationName = navState.organisationName ?? '—'

  const [txData, setTxData] = useState<audit.ListTransactionsResult | null>(null)
  const [txLoading, setTxLoading] = useState(false)
  const [txError, setTxError] = useState<string | null>(null)

  const [ordData, setOrdData] = useState<audit.ListOrdersResult | null>(null)
  const [ordLoading, setOrdLoading] = useState(false)
  const [ordError, setOrdError] = useState<string | null>(null)

  const patchSearch = useCallback(
    (patch: Partial<typeof listSearch>) => {
      const next = buildAuditListSearch({ ...listSearch, ...patch })
      setSearchParams(next, { replace: true })
    },
    [listSearch, setSearchParams],
  )

  const loadTx = useCallback(async () => {
    if (invalidStore) return
    setTxError(null)
    setTxLoading(true)
    try {
      const data = await audit.listAdminStoreTransactions(storeId, {
        page: txPage,
        limit: TX_LIMIT,
        channel,
      })
      setTxData(data)
    } catch (e) {
      setTxError(e instanceof Error ? e.message : 'Failed to load')
      setTxData(null)
    } finally {
      setTxLoading(false)
    }
  }, [invalidStore, storeId, txPage, channel])

  const loadOrd = useCallback(async () => {
    if (invalidStore) return
    setOrdError(null)
    setOrdLoading(true)
    try {
      const data = await audit.listAdminStoreOrders(storeId, {
        page: ordPage,
        limit: ORD_LIMIT,
        status: ordStatus.trim() || undefined,
        paymentStatus: ordPayment.trim() || undefined,
      })
      setOrdData(data)
    } catch (e) {
      setOrdError(e instanceof Error ? e.message : 'Failed to load')
      setOrdData(null)
    } finally {
      setOrdLoading(false)
    }
  }, [invalidStore, storeId, ordPage, ordStatus, ordPayment])

  useEffect(() => {
    if (invalidStore) return
    if (tab === 'tx') void loadTx()
    else void loadOrd()
  }, [invalidStore, tab, loadTx, loadOrd])

  const txTotal = txData?.total ?? 0
  const txFrom = txTotal === 0 ? 0 : (txPage - 1) * TX_LIMIT + 1
  const txTo = Math.min(txPage * TX_LIMIT, txTotal)

  const ordTotal = ordData?.total ?? 0
  const ordFrom = ordTotal === 0 ? 0 : (ordPage - 1) * ORD_LIMIT + 1
  const ordTo = Math.min(ordPage * ORD_LIMIT, ordTotal)

  function setTab(nextTab: AuditTab) {
    patchSearch({ tab: nextTab })
  }

  function openTransactionDetail(detailRef: string) {
    navigate(storeAuditTransactionPath(storeId, detailRef, searchParams), {
      state: navState,
    })
  }

  const txInitialLoading = txLoading && !txData?.items?.length
  const ordInitialLoading = ordLoading && !ordData?.items?.length

  return (
    <div className="mx-auto w-full max-w-6xl space-y-8 pb-10">
      <Card padding={false} className="overflow-hidden">
        <div className="border-b border-border bg-gradient-to-br from-muted/40 via-muted/20 to-transparent px-6 py-6 sm:px-8 sm:py-8 [&>div]:mb-0">
          <CardHeader
            title="Store audit"
            description="Review harmonized transactions and orders before approving merchant withdrawals."
          />
        </div>

        <div className="space-y-6 px-6 py-6 sm:px-8 sm:pb-8">
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <Link to="/withdrawals" className="text-primary underline-offset-4 hover:underline">
              ← Back to withdrawals
            </Link>
          </div>

          {invalidStore ? (
            <p className="text-sm text-destructive" role="alert">
              Invalid store in URL.
            </p>
          ) : (
            <>
              <div>
                <p className="font-medium text-foreground">{storeName}</p>
                <p className="text-xs text-muted-foreground">{organisationName}</p>
                <p className="text-xs text-muted-foreground">Store #{storeId}</p>
                {navState.fromWithdrawalId != null ? (
                  <p className="mt-1 text-xs text-muted-foreground">
                    Opened from withdrawal #{navState.fromWithdrawalId}
                  </p>
                ) : null}
              </div>

              <div className="flex flex-wrap gap-2 border-b border-border pb-2">
                <button
                  type="button"
                  className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                    tab === 'tx'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                  }`}
                  onClick={() => setTab('tx')}
                >
                  Transactions
                </button>
                <button
                  type="button"
                  className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                    tab === 'orders'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                  }`}
                  onClick={() => setTab('orders')}
                >
                  Orders
                </button>
              </div>

              {tab === 'tx' ? (
                <div className="space-y-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <label className="flex items-center gap-2 text-muted-foreground">
                      <span>Channel</span>
                      <select
                        className="min-h-10 rounded-xl border border-border bg-card px-3 py-2 text-sm text-foreground"
                        value={channel}
                        onChange={(e) => {
                          patchSearch({
                            channel: e.target.value as TxChannel,
                            txPage: 1,
                          })
                        }}
                      >
                        {TX_CHANNELS.map((c) => (
                          <option key={c.value} value={c.value}>
                            {c.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <Button type="button" variant="secondary" size="sm" onClick={() => void loadTx()} disabled={txLoading}>
                      Refresh
                    </Button>
                  </div>

                  {txError ? (
                    <p
                      className="rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
                      role="alert"
                    >
                      {txError}
                    </p>
                  ) : null}

                  {txInitialLoading ? (
                    <SkeletonTable rows={8} columns={5} />
                  ) : (
                    <div className="overflow-x-auto rounded-2xl border border-border/90 bg-card/30">
                      <Table variant="plain" className="!rounded-none !border-0 !shadow-none">
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-12 whitespace-nowrap">S/N</TableHead>
                            <TableHead>When</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead>Amount</TableHead>
                            <TableHead>Detail</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {!txData?.items?.length ? (
                            <TableRow>
                              <td colSpan={5} className="px-4 py-12 text-center text-muted-foreground">
                                No rows for this page.
                              </td>
                            </TableRow>
                          ) : (
                            txData.items.map((row, i) => {
                              const p = row.payload
                              const detailRef = transactionDetailRef(row)
                              const ref = String(p.detailRef ?? '')
                              const orderRef = p.orderReference != null ? String(p.orderReference) : ''
                              const wStatus = p.status != null ? String(p.status) : ''
                              const entry = p.entryType != null ? String(p.entryType) : ''
                              let detail = ref
                              if (orderRef) detail += ` · Order ${orderRef}`
                              if (wStatus) detail += ` · ${wStatus}`
                              if (entry && row.kind === 'ledger') detail += ` · ${entry}`
                              return (
                                <TableRow
                                  key={`${row.kind}-${ref}-${i}`}
                                  className={detailRef ? 'cursor-pointer hover:bg-muted/40' : undefined}
                                  onClick={detailRef ? () => openTransactionDetail(detailRef) : undefined}
                                  onKeyDown={
                                    detailRef
                                      ? (e) => {
                                          if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault()
                                            openTransactionDetail(detailRef)
                                          }
                                        }
                                      : undefined
                                  }
                                  tabIndex={detailRef ? 0 : undefined}
                                  role={detailRef ? 'button' : undefined}
                                >
                                  <TableCell className="tabular-nums text-muted-foreground">
                                    {(txPage - 1) * TX_LIMIT + i + 1}
                                  </TableCell>
                                  <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                                    {new Date(row.createdAt).toLocaleString()}
                                  </TableCell>
                                  <TableCell>{txKindLabel(row.kind)}</TableCell>
                                  <TableCell className="tabular-nums">
                                    {row.currency} {row.amount}
                                  </TableCell>
                                  <TableCell className="max-w-[320px] break-words text-xs">
                                    {detail}
                                    {detailRef ? (
                                      <span className="mt-1 block text-[11px] font-medium text-primary">View details</span>
                                    ) : null}
                                  </TableCell>
                                </TableRow>
                              )
                            })
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  )}

                  {txTotal > 0 ? (
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <p className="text-xs text-muted-foreground">
                        Showing {txFrom}–{txTo} of {txTotal} (paged over harmonized activity)
                      </p>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          disabled={txPage <= 1 || txLoading}
                          onClick={() => patchSearch({ txPage: Math.max(1, txPage - 1) })}
                        >
                          Previous
                        </Button>
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          disabled={txPage * TX_LIMIT >= txTotal || txLoading}
                          onClick={() => patchSearch({ txPage: txPage + 1 })}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex flex-wrap gap-2">
                    <input
                      className="min-h-10 rounded-xl border border-border bg-card px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground"
                      placeholder="Order status filter"
                      value={ordStatus}
                      onChange={(e) => patchSearch({ ordStatus: e.target.value, ordPage: 1 })}
                    />
                    <input
                      className="min-h-10 rounded-xl border border-border bg-card px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground"
                      placeholder="Payment status filter"
                      value={ordPayment}
                      onChange={(e) => patchSearch({ ordPayment: e.target.value, ordPage: 1 })}
                    />
                    <Button type="button" variant="secondary" size="sm" onClick={() => void loadOrd()} disabled={ordLoading}>
                      Apply / refresh
                    </Button>
                  </div>

                  {ordError ? (
                    <p
                      className="rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
                      role="alert"
                    >
                      {ordError}
                    </p>
                  ) : null}

                  {ordInitialLoading ? (
                    <SkeletonTable rows={8} columns={6} />
                  ) : (
                    <div className="overflow-x-auto rounded-2xl border border-border/90 bg-card/30">
                      <Table variant="plain" className="!rounded-none !border-0 !shadow-none">
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-12 whitespace-nowrap">S/N</TableHead>
                            <TableHead>Reference</TableHead>
                            <TableHead>Order status</TableHead>
                            <TableHead>Payment</TableHead>
                            <TableHead>Total</TableHead>
                            <TableHead>When</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {!ordData?.items?.length ? (
                            <TableRow>
                              <td colSpan={6} className="px-4 py-12 text-center text-muted-foreground">
                                No orders for this page.
                              </td>
                            </TableRow>
                          ) : (
                            ordData.items.map((o, i) => (
                              <TableRow key={o.id}>
                                <TableCell className="tabular-nums text-muted-foreground">
                                  {(ordPage - 1) * ORD_LIMIT + i + 1}
                                </TableCell>
                                <TableCell className="font-mono text-xs">{o.orderReference}</TableCell>
                                <TableCell>{o.status}</TableCell>
                                <TableCell>
                                  <div>{o.paymentStatus}</div>
                                  {o.paymentMethod ? (
                                    <div className="text-xs text-muted-foreground">{o.paymentMethod}</div>
                                  ) : null}
                                </TableCell>
                                <TableCell className="tabular-nums">
                                  {o.currency} {o.total}
                                </TableCell>
                                <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                                  {new Date(o.createdAt).toLocaleString()}
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  )}

                  {ordTotal > 0 ? (
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <p className="text-xs text-muted-foreground">
                        Showing {ordFrom}–{ordTo} of {ordTotal}
                      </p>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          disabled={ordPage <= 1 || ordLoading}
                          onClick={() => patchSearch({ ordPage: Math.max(1, ordPage - 1) })}
                        >
                          Previous
                        </Button>
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          disabled={ordPage * ORD_LIMIT >= ordTotal || ordLoading}
                          onClick={() => patchSearch({ ordPage: ordPage + 1 })}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  ) : null}
                </div>
              )}
            </>
          )}
        </div>
      </Card>
    </div>
  )
}
